<script>
// Gráfico "Projeção ano a ano" — merge de custo (bps) + patrimônio (R$) do
// cliente ao longo do horizonte, com toggle pra alternar a visão. Usa o
// <line-chart> reativo da plataforma (Chart.js v2) alimentado por
// formatLines. 3 séries: comissionado (tracejado), fee fixo · assessoria e
// consultor CVM.

import { formatLines } from '@utils/GraficoData'
import { bps } from '@modules/fee-fixo/utils/fee-lab-format'
import { computePatrimony } from '@modules/fee-fixo/utils/Utils'
import {
  REGIME_CHART_COLORS,
  CHART_STYLES,
} from '@modules/fee-fixo/constants/feeLab'

const VIEW_OPTIONS = [
  { text: 'Custo (bps)', value: 'custo' },
  { text: 'Patrimônio (R$)', value: 'patrimonio' },
]

// Aplica os marcadores de círculo do v0: vazado + tracejado no comissionado,
// preenchido nos outros 2. `elements.point.radius` global de formatLines é
// 0.1 (linha sem marcador) — override local por dataset, mesmo padrão já
// usado pro borderDash do comissionado nos gráficos antigos.
const aplicarMarcadores = (datasets, corComis) => {
  datasets.forEach((ds) => {
    ds.pointRadius = 4
    ds.pointHoverRadius = 5
    ds.pointBorderWidth = 2
    if (ds.borderColor === corComis) {
      ds.borderDash = [6, 4]
      ds.pointBackgroundColor = '#FFFFFF'
      ds.pointBorderColor = corComis
    } else {
      ds.pointBackgroundColor = ds.borderColor
      ds.pointBorderColor = ds.borderColor
    }
  })
  return datasets
}

export default {
  name: 'FeeLabProjecaoChart',
  props: {
    resultado: {
      type: Object,
      default: null,
    },
    state: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      chartStyles: CHART_STYLES,
      viewOptions: VIEW_OPTIONS,
      viewMode: 'custo',
    }
  },
  computed: {
    years() {
      const r = this.resultado
      return r && Array.isArray(r.years) ? r.years : []
    },
    series() {
      const r = this.resultado
      const s = (r && r.series) || {}
      return {
        comis: Array.isArray(s.comis) ? s.comis : [],
        fee: Array.isArray(s.fee) ? s.fee : [],
        cons: Array.isArray(s.cons) ? s.cons : [],
      }
    },
    hasData() {
      return this.years.length > 1
    },
    labels() {
      return this.years.map((yr) => (yr === 0 ? 'Hoje' : 'Ano ' + yr))
    },
    subtitulo() {
      return this.viewMode === 'custo'
        ? 'Custo total do cliente (bps a.a.) conforme a carteira migra'
        : 'Patrimônio do cliente (custo + alfa do serviço capitalizados)'
    },
    // Visão "Custo (bps)": valores crus em bps (sem converter pra %), como
    // no v0 — eixo Y mostra "210", "180"... em vez de "1,40%", "1,35%".
    chartCusto() {
      const s = this.series
      const dados = {
        mode: 'numero',
        labels: this.labels,
        data: [
          {
            label: 'Comissionado',
            dados: s.comis,
            color: REGIME_CHART_COLORS.comis,
          },
          {
            label: 'Fee fixo · assessoria',
            dados: s.fee,
            color: REGIME_CHART_COLORS.fee,
          },
          {
            label: 'Consultor CVM',
            dados: s.cons,
            color: REGIME_CHART_COLORS.cons,
          },
        ],
      }
      const { chartOptions, chartData } = formatLines(dados)
      aplicarMarcadores(chartData.datasets, REGIME_CHART_COLORS.comis)
      // mode: 'numero' não formata o tooltip — sobrescreve pra "120 bps".
      chartOptions.tooltips.callbacks.label = (tooltipItem, data) => {
        const dataset = data.datasets[tooltipItem.datasetIndex]
        return dataset.label + ': ' + bps(tooltipItem.yLabel)
      }
      chartOptions.maintainAspectRatio = false
      return { chartOptions, chartData }
    },
    // Visão "Patrimônio (R$)": séries anuais recompostas a partir de
    // resultado.series + state (auc/rent/alfa) — mesma lógica de
    // computePatrimony já usada no gráfico de patrimônio anterior.
    chartPatrimonio() {
      const s = this.series
      const st = this.state || {}
      const p = computePatrimony(
        this.years,
        s,
        Number(st.auc) || 0,
        Number(st.rent) || 0,
        Number(st.alfa) || 0,
        Number(st.aporteAnual) || 0
      )
      const dados = {
        mode: 'R$',
        labels: this.labels,
        data: [
          {
            label: 'Comissionado',
            dados: p.comis,
            color: REGIME_CHART_COLORS.comis,
          },
          {
            label: 'Fee fixo · assessoria',
            dados: p.fee,
            color: REGIME_CHART_COLORS.fee,
          },
          {
            label: 'Consultor CVM',
            dados: p.cons,
            color: REGIME_CHART_COLORS.cons,
          },
        ],
      }
      const { chartOptions, chartData } = formatLines(dados)
      aplicarMarcadores(chartData.datasets, REGIME_CHART_COLORS.comis)
      chartOptions.maintainAspectRatio = false
      return { chartOptions, chartData }
    },
    chart() {
      return this.viewMode === 'custo' ? this.chartCusto : this.chartPatrimonio
    },
    chartData() {
      return this.chart.chartData
    },
    chartOptions() {
      return this.chart.chartOptions
    },
  },
}
</script>

<template>
  <div class="fee-lab-projecao">
    <div
      class="d-flex align-center justify-space-between flex-wrap az-gap-small mb-1"
    >
      <div>
        <h3 class="az-text-primary az-size-14 font-semibold">
          Projeção ano a ano
        </h3>
        <p class="az-text-small ma-0 mt-1">{{ subtitulo }}</p>
      </div>
      <BaseToggleButton
        v-model="viewMode"
        small
        buttonStyle="flatGray"
        :options="viewOptions"
      />
    </div>
    <div v-if="hasData" class="fee-lab-projecao__canvas mt-4">
      <line-chart
        :key="viewMode"
        :chart-data="chartData"
        :options="chartOptions"
        :styles="chartStyles"
      />
    </div>
    <div v-else class="fee-lab-projecao__empty" />
  </div>
</template>

<style lang="sass" scoped>
.fee-lab-projecao
  width: 100%

.fee-lab-projecao__canvas
  position: relative
  height: 260px
</style>
